<template>
  <div class="community-post-card">
    <div class="post-header d-flex align-items-center">
      <div class="avatar">
        <img v-if="authorPhoto" :src="authorPhoto" :alt="authorName" />
        <span v-else class="letter">{{ authorInitial }}</span>
      </div>

      <div class="post-author flex-fill">
        <div class="d-flex align-items-center flex-wrap">
          <span class="author-name">{{ authorName }}</span>
          <span v-if="isModeratorAuthor" class="badge-moderator ml-2">
            Moderador
          </span>
          <span v-if="post.is_pinned" class="badge-pinned ml-2">
            <i class="icon-star" /> Fixado
          </span>
          <span v-if="post.is_scheduled" class="badge-scheduled ml-2">
            Agendado
          </span>
        </div>
        <small class="post-date">
          {{ dateLabel }}
          <template v-if="post.edited_at"> &middot; editado</template>
        </small>
      </div>

      <b-dropdown
        v-if="post.can_manage"
        variant="link"
        toggle-class="text-decoration-none"
        no-caret
        right
      >
        <template #button-content>
          <i class="icon-options" />
        </template>
        <b-dropdown-item @click="startEdit">
          <i class="icon-edit-filled" /> Editar
        </b-dropdown-item>
        <b-dropdown-item @click="removing = true">
          <i class="icon-trash" />
          {{ post.is_scheduled ? "Cancelar" : "Excluir" }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="post-body mt-2" v-html="post.text" />

    <div v-if="post.image" class="post-image mt-2">
      <img :src="post.image" alt="" />
    </div>

    <div class="post-actions d-flex align-items-center mt-3">
      <button class="btn-interaction" :class="{ liked }" @click="toggleLike">
        <i :class="liked ? 'icon-liked' : 'icon-like'" />
        <span class="ml-1">{{ likesCount }}</span>
      </button>
      <button
        class="btn-interaction ml-4"
        @click="showComments = !showComments"
      >
        <i class="icon-talk" />
        <span class="ml-1">{{ commentsCount }}</span>
      </button>
    </div>

    <comment-thread
      v-if="showComments"
      class="mt-3"
      :post-id="post.id"
      :can-comment="canComment"
      :allow-nested-reply="isForumPost"
      @count-change="onCommentCountChange"
    />

    <b-modal
      v-model="editing"
      hide-header
      hide-footer
      centered
      size="lg"
      :modal-class="`theme-${$setup.theme}-modals`"
    >
      <div class="post-edit-modal">
        <h5 class="mb-4">Editar publicação</h5>
        <rich-text-editor v-model="editText" />

        <div v-if="editImage" class="composer-image mt-2">
          <img :src="editImage" alt="" />
          <button
            type="button"
            class="btn-remove-image"
            @click="editImage = null"
          >
            Remover imagem
          </button>
        </div>

        <post-producer-controls
          v-if="canModerate"
          :publish-at.sync="editPublishAt"
          :is-pinned.sync="editIsPinned"
          :moderator-profile-code.sync="editModeratorProfileCode"
        />

        <div class="d-flex align-items-center justify-content-between mt-3">
          <input-image :id="`edit-image-${post.id}`" v-model="editImage">
            <span class="btn-attach"><i class="icon-image" /> Imagem</span>
          </input-image>
          <div>
            <button
              class="btn btn-outline-secondary mr-2"
              @click="editing = false"
            >
              Cancelar
            </button>
            <button
              class="btn btn-primary"
              :disabled="loading"
              @click="saveEdit"
            >
              <loading :show="loading" small>Salvar</loading>
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="removing"
      hide-header
      hide-footer
      centered
      :modal-class="`theme-${$setup.theme}-modals`"
    >
      <div class="post-remove-modal text-center">
        <h5 class="mb-4">{{ removeTitle }}</h5>
        <div class="d-flex align-items-center justify-content-center">
          <button
            class="btn btn-outline-secondary mr-3"
            @click="removing = false"
          >
            Voltar
          </button>
          <button class="btn btn-primary" :disabled="loading" @click="remove">
            <loading :show="loading" small>{{ removeConfirmLabel }}</loading>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { timeFromNow, toHumanDate } from "@/functions/helpers";
import InputImage from "@/components/InputImage.vue";
import RichTextEditor from "./RichTextEditor.vue";
import CommentThread from "./CommentThread.vue";
import PostProducerControls from "./PostProducerControls.vue";

export default {
  name: "PostCard",
  components: {
    InputImage,
    RichTextEditor,
    CommentThread,
    PostProducerControls
  },
  props: {
    post: {
      type: Object,
      required: true
    },
    canModerate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      liked: this.post.liked_by_me,
      likesCount: this.post.likes_count,
      commentsCount: this.post.comments_count,
      showComments: false,
      editing: false,
      removing: false,
      loading: false,
      editText: this.post.text,
      editImage: this.post.image,
      editPublishAt: this.post.publish_at || null,
      editIsPinned: !!this.post.is_pinned,
      editModeratorProfileCode: this.currentProfileCode()
    };
  },
  computed: {
    author() {
      return this.post.author || {};
    },
    authorName() {
      return this.author.name || "Usuário";
    },
    authorPhoto() {
      return this.author.photo;
    },
    authorInitial() {
      return (this.authorName[0] || "?").toUpperCase();
    },
    isModeratorAuthor() {
      return !!this.author.is_moderator;
    },
    isForumPost() {
      return this.post.kind === "forum";
    },
    canComment() {
      if (this.isForumPost) return true;
      if (this.post.can_manage) return true;
      return this.post.allow_comments !== false;
    },
    dateLabel() {
      if (this.post.is_scheduled && this.post.publish_at) {
        return `Agendado para ${toHumanDate(
          this.post.publish_at.slice(0, 10)
        )}`;
      }
      return timeFromNow(this.post.created_at);
    },
    removeTitle() {
      return this.post.is_scheduled
        ? "Cancelar esta publicação agendada?"
        : "Tem certeza que deseja excluir esta publicação?";
    },
    removeConfirmLabel() {
      return this.post.is_scheduled ? "Sim, cancelar" : "Sim, excluir";
    }
  },
  methods: {
    currentProfileCode() {
      return this.post.author && this.post.author.is_moderator
        ? this.post.author.profile_code || null
        : null;
    },
    startEdit() {
      this.editText = this.post.text;
      this.editImage = this.post.image;
      this.editPublishAt = this.post.publish_at || null;
      this.editIsPinned = !!this.post.is_pinned;
      this.editModeratorProfileCode = this.currentProfileCode();
      this.editing = true;
    },
    toggleLike() {
      const previousLiked = this.liked;
      const previousCount = this.likesCount;
      this.liked = !previousLiked;
      this.likesCount = previousCount + (this.liked ? 1 : -1);
      this.$store
        .dispatch("community/like", this.post.id)
        .then(data => {
          this.liked = data.liked;
          this.likesCount = data.likes_count;
        })
        .catch(() => {
          this.liked = previousLiked;
          this.likesCount = previousCount;
        });
    },
    saveEdit() {
      this.loading = true;
      const payload = { postId: this.post.id, text: this.editText };
      if (this.editImage !== this.post.image) payload.image = this.editImage;
      let action = "community/editPost";
      if (this.canModerate) {
        action = "community/editProducerPost";
        payload.publish_at = this.editPublishAt;
        payload.is_pinned = this.editIsPinned;
        payload.moderator_profile_code = this.editModeratorProfileCode;
      }
      this.$store
        .dispatch(action, payload)
        .then(updated => {
          this.$message.success("Publicação atualizada");
          this.editing = false;
          this.$emit("updated", updated);
        })
        .catch(error => {
          const message =
            error?.response?.data?.moderator_profile_code?.[0] ||
            error?.response?.data?.non_field_errors?.[0] ||
            "Não foi possível salvar";
          this.$message.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    remove() {
      this.loading = true;
      const action = this.canModerate
        ? "community/cancelPost"
        : "community/deletePost";
      this.$store
        .dispatch(action, this.post.id)
        .then(() => {
          this.$message.success(
            this.post.is_scheduled
              ? "Publicação cancelada"
              : "Publicação excluída"
          );
          this.removing = false;
          this.$emit("deleted", this.post.id);
        })
        .catch(() => this.$message.error("Não foi possível excluir"))
        .finally(() => {
          this.loading = false;
        });
    },
    onCommentCountChange(count) {
      this.commentsCount = count;
    }
  }
};
</script>

<style lang="scss" scoped>
.community-post-card {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(128, 128, 128, 0.2);

  .post-header {
    gap: 12px;

    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(128, 128, 128, 0.2);
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .letter {
        font-size: 16px;
        font-weight: 500;
      }
    }

    .author-name {
      font-weight: 500;
      font-size: 14px;
    }

    .badge-pinned,
    .badge-scheduled,
    .badge-moderator {
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 12px;
      background: rgba(128, 128, 128, 0.16);

      i {
        font-size: 12px;
      }
    }

    .badge-scheduled {
      background: rgba(255, 179, 71, 0.18);
      color: #d98a00;
    }

    .badge-moderator {
      background: rgba(90, 120, 255, 0.16);
      color: #4a63d8;
    }

    .post-date {
      font-size: 12px;
      opacity: 0.7;
    }
  }

  .post-body {
    overflow-wrap: anywhere;
    font-size: 14px;
    line-height: 1.5;

    ::v-deep a {
      text-decoration: underline;
    }
  }

  .post-image {
    img {
      max-width: 100%;
      border-radius: 8px;
    }
  }

  .post-actions {
    .btn-interaction {
      border: none;
      background: transparent;
      padding: 0;
      color: inherit;
      display: inline-flex;
      align-items: center;
      font-size: 14px;

      i {
        font-size: 22px;
      }

      &.liked i {
        color: #ff757e;
      }
    }
  }

  .composer-image {
    position: relative;
    display: inline-block;

    img {
      max-width: 100%;
      max-height: 240px;
      border-radius: 8px;
    }

    .btn-remove-image {
      display: block;
      margin-top: 4px;
      border: none;
      background: transparent;
      color: #ff757e;
      font-size: 12px;
      padding: 0;
    }
  }

  .btn-attach {
    display: inline-flex;
    align-items: center;
    font-size: 14px;

    i {
      font-size: 18px;
      margin-right: 6px;
    }
  }
}
</style>
